import { Link } from 'react-router';
import { LIBELLE_CATEGORIE_PRESENCE, type BlocPresences, type CategoriePresence, type StatutPeriodePresence } from '@/api/types';
import { Kpi } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, dateFr } from '@/lib/utils';

const CLASSE_CATEGORIE: Record<CategoriePresence, string> = {
  PRESTE: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  MISSION: 'border-sky-200 bg-sky-50 text-sky-800',
  INCAPACITE: 'border-amber-200 bg-amber-50 text-amber-800',
  CONGE_ANNUEL: 'border-indigo-200 bg-indigo-50 text-indigo-800',
  CONGE_CIRCONSTANCE: 'border-indigo-200 bg-indigo-50 text-indigo-800',
  CONGE_ACCOUCHEMENT: 'border-indigo-200 bg-indigo-50 text-indigo-800',
  FERIE: 'border-slate-200 bg-slate-100 text-slate-700',
  REPOS: 'border-slate-200 bg-slate-50 text-slate-500',
  ABSENCE_AUTORISEE_NON_REMUNEREE: 'border-orange-200 bg-orange-50 text-orange-800',
  ABSENCE_INJUSTIFIEE: 'border-red-200 bg-red-50 text-red-800',
  MISE_A_PIED: 'border-red-300 bg-red-100 text-red-900',
};

export const CategoriePresenceBadge = ({ categorie, fraction }: { categorie: CategoriePresence; fraction?: number }) => (
  <Badge variant="outline" className={cn('whitespace-nowrap', CLASSE_CATEGORIE[categorie])}>
    {LIBELLE_CATEGORIE_PRESENCE[categorie]}
    {fraction !== undefined && fraction > 0 && fraction < 1 ? ` (½)` : ''}
  </Badge>
);

export const StatutPeriodeBadge = ({ statut }: { statut: StatutPeriodePresence }) => (
  <Badge variant={statut === 'OUVERTE' ? 'default' : 'outline'} className={cn(statut === 'CLOTUREE' && 'border-slate-300 bg-slate-100 text-slate-700')}>
    {statut === 'OUVERTE' ? 'Ouverte' : 'Clôturée'}
  </Badge>
);

/** Bloc du tableau de bord (lot HP-3) : ses présences pour tout agent ; validations pour les responsables ; état de la période pour la paie. */
export function BlocPresencesTableau({ bloc }: { bloc: BlocPresences }) {
  if (!bloc.periode) return null;
  const p = bloc.periode;
  return (
    <Card data-testid="bloc-presences">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Présences · période {p.mois}
          <span className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/rh/mes-presences">Mes présences</Link>
            </Button>
            {(bloc.responsable || bloc.gestion) && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/paie/presences?mois=${p.mois}`}>Registre des présences</Link>
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>
          Du {dateFr(p.debut)} au {dateFr(p.fin)} (D17) · variables attendues avant le {dateFr(p.dateLimiteVariables)} · {p.statut === 'OUVERTE' ? 'période ouverte' : 'période clôturée'}.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {bloc.mien && (
          <Link to="/rh/mes-presences">
            <Kpi label="Mes jours prestés" valeur={bloc.mien.prestes} detail={`${bloc.mien.absences} jour(s) non rémunéré(s) · ${bloc.mien.aConfirmer} présumé(s) · ${bloc.mien.heuresSup} h sup. validées`} accent={bloc.mien.absences > 0 ? 'alerte' : undefined} />
          </Link>
        )}
        {bloc.responsable && (
          <>
            <Link to={`/paie/presences?mois=${p.mois}&onglet=validation`}>
              <Kpi label="Structures à valider" valeur={bloc.responsable.aValider} detail={`${bloc.responsable.structures} structure(s) sous votre responsabilité`} accent={bloc.responsable.aValider > 0 ? 'alerte' : 'ok'} />
            </Link>
            <Link to={`/paie/presences?mois=${p.mois}&anomalies=true`}>
              <Kpi label="Anomalies et déclarations" valeur={bloc.responsable.anomalies + bloc.responsable.heuresSupDeclarees} detail={`${bloc.responsable.anomalies} anomalie(s) de pointage · ${bloc.responsable.heuresSupDeclarees} heure(s) sup. à valider`} accent={bloc.responsable.anomalies + bloc.responsable.heuresSupDeclarees > 0 ? 'alerte' : undefined} />
            </Link>
          </>
        )}
        {bloc.gestion && (
          <>
            <Link to={`/paie/presences?mois=${p.mois}&onglet=validation`}>
              <Kpi label="Structures validées" valeur={`${bloc.gestion.validees} / ${bloc.gestion.structures}`} detail={p.statut === 'OUVERTE' ? 'avant clôture des présences' : 'période clôturée'} accent={bloc.gestion.validees === bloc.gestion.structures ? 'ok' : 'alerte'} />
            </Link>
            <Link to={`/paie/presences?mois=${p.mois}&anomalies=true`}>
              <Kpi label="À régler" valeur={bloc.gestion.anomalies + bloc.gestion.heuresSupDeclarees} detail={`${bloc.gestion.anomalies} anomalie(s) · ${bloc.gestion.heuresSupDeclarees} déclaration(s) · ${bloc.gestion.importations} importation(s)`} accent={bloc.gestion.anomalies + bloc.gestion.heuresSupDeclarees > 0 ? 'alerte' : undefined} />
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
